import { useMemo } from 'react'
import { buildMacroVolumePhaseSegments } from 'functions/macroVolumePhaseUtils'

const PHASE_MIN_WIDTH_PX = 140
const CHART_HEIGHT_PX = 104

export default function ProgramVolumeConfigs({
    macroVolumePhases,
    macroVolumePhaseTypes,
    macroVolumePhaseTypeHashMap,
    programLengthLowerBound,
    programLengthUpperBound,
    deloadAfterEvent = true,
}) {
    const previewProgramLength = programLengthUpperBound

    const { segments } = useMemo(() => (
        buildMacroVolumePhaseSegments({
            macroVolumePhaseTypes,
            macroVolumePhases,
            macroVolumePhaseTypeHashMap,
            programLength: previewProgramLength,
            deloadAfterEvent,
        })
    ), [
        macroVolumePhaseTypes,
        macroVolumePhases,
        macroVolumePhaseTypeHashMap,
        previewProgramLength,
        deloadAfterEvent,
    ])

    if (macroVolumePhases.length === 0) {
        return (
            <div
                className='flex items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400'
                style={{ height: CHART_HEIGHT_PX }}
            >
                No phases configured
            </div>
        )
    }

    if (segments.length === 0) {
        return (
            <div
                className='flex items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400'
                style={{ height: CHART_HEIGHT_PX }}
            >
                No active phases at this program length
            </div>
        )
    }

    const chartMinWidth = `max(100%, ${segments.length * PHASE_MIN_WIDTH_PX}px)`

    return (
        <div
            className='flex flex-col gap-2 overflow-hidden'
            style={{ height: CHART_HEIGHT_PX }}
        >
            <div className='shrink-0 overflow-x-auto'>
                <div
                    className='flex h-10 overflow-hidden rounded-lg shadow-inner ring-1 ring-black/5'
                    style={{ minWidth: chartMinWidth }}
                >
                    {segments.map((segment, index) => (
                        <div
                            key={`${segment.macroVolumePhaseTypeId}-${index}`}
                            className={`flex h-full min-w-0 items-center justify-center px-1.5 ${index > 0 ? 'border-l border-white/30' : ''}`}
                            style={{
                                flexGrow: segment.weeks,
                                flexBasis: 0,
                                flexShrink: 0,
                                minWidth: PHASE_MIN_WIDTH_PX,
                                backgroundColor: segment.colorHex,
                            }}
                            title={`${segment.name} · ${segment.weeks} wk${segment.weeks === 1 ? '' : 's'} · ${segment.durationLabel}`}
                        >
                            <p className='truncate text-center text-xs font-medium text-gray-900'>
                                {segment.name} ({segment.weeks} wk{segment.weeks === 1 ? '' : 's'})
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            <div className='flex min-h-0 flex-1 flex-col justify-end gap-1 overflow-hidden'>
                <div className='flex flex-wrap gap-x-3 gap-y-0.5 overflow-hidden'>
                    {segments.map((segment, index) => (
                        <div
                            key={`legend-${segment.macroVolumePhaseTypeId}-${index}`}
                            className='flex items-center gap-1'
                        >
                            <span
                                className='h-2 w-2 shrink-0 rounded-full'
                                style={{ backgroundColor: segment.colorHex }}
                            />
                            <span className='text-[11px] leading-tight text-gray-600'>
                                {segment.name} · {segment.durationLabel} · {segment.weeks} wk{segment.weeks === 1 ? '' : 's'}
                            </span>
                        </div>
                    ))}
                </div>

                <p className='truncate text-[11px] leading-tight text-gray-400'>
                    {programLengthLowerBound}–{programLengthUpperBound} wk programs · Preview at {previewProgramLength} wk
                    {deloadAfterEvent ? ' · Post-race deload' : ''}
                </p>
            </div>
        </div>
    )
}
